/* What the bell sent you here to look at.
 *
 * A notification carries an entity and, when it is a reply, the comment inside
 * it. The URL carries the same pair — ?request=…&comment=… — so the target
 * survives a refresh and can be pasted to somebody else.
 *
 * Read through a hook rather than inline because two pages need it and the
 * clearing rule is easy to get wrong: the params must go once the reader has
 * arrived, or the thread springs back open every time they return to the tab.
 */
import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

export interface FocusTarget {
  /** The post or Prayer Request to open. */
  entityId: string | null
  /** The comment to scroll to inside it, when there is one. */
  commentId: string | null
  /** Drop both from the URL, once the reader has seen the thing. */
  clear: () => void
}

export function useFocusTarget(entityParam: 'request' | 'post'): FocusTarget {
  const [params, setParams] = useSearchParams()

  const clear = useCallback(() => {
    const next = new URLSearchParams(params)
    if (!next.has(entityParam) && !next.has('comment')) return
    next.delete(entityParam)
    next.delete('comment')
    // Replace rather than push: Back should return to wherever they came from,
    // not to the same page with the highlight lit again.
    setParams(next, { replace: true })
  }, [params, setParams, entityParam])

  return {
    entityId: params.get(entityParam),
    commentId: params.get('comment'),
    clear,
  }
}
